import { tva } from '@gluestack-ui/utils/nativewind-utils'
import React from 'react'

import { Box } from '../box'
import { Text } from '../text'

export type UnitTaskCardStatus = 'critical' | 'warning' | 'success' | 'neutral'

const unitTaskCardStyle = tva({
    base: 'gap-3 rounded border-2 bg-aos-bg-surface p-3',
    variants: {
        status: {
            critical: 'border-status-critical',
            warning: 'border-status-warning',
            success: 'border-status-success',
            neutral: 'border-status-neutral',
        },
        active: {
            true: 'border-solid',
            false: 'border-dashed',
        },
    },
    defaultVariants: {
        active: true,
    },
})

export type UnitTaskCardProps = {
    /** Drives the border colour (To do=critical, In progress=warning, Done=success, Skipped=neutral). */
    status: UnitTaskCardStatus
    /** Solid border (the user's own unit) vs dashed (another unit / read-only). */
    active?: boolean
    /** Header label (Figma uppercase unit role, e.g. "RESCUE SERVICE"). */
    title: string
    /** Header trailing slot — the status dropdown. */
    statusSlot?: React.ReactNode
    /** Footer line bottom-right (Figma `modifiedBy | time`), muted caption. */
    footer?: React.ReactNode
    /** Body — description text, the note box, etc. */
    children?: React.ReactNode
    className?: string
    testID?: string
}

/**
 * Unit Task List checklist card (Figma `UnitChecklistItem`, node 11569:118385):
 * a `bg/surface` card with a 2px status-coloured border — solid when the task
 * belongs to the viewer's own unit (active), dashed otherwise (inactive). The
 * Figma redesign drops the legacy glow shadow + 4px border. Header is the
 * uppercase unit title + the status dropdown slot; the footer sits bottom-right.
 */
export const UnitTaskCard: React.FC<UnitTaskCardProps> = ({
    status,
    active = true,
    title,
    statusSlot,
    footer,
    children,
    className,
    testID,
}) => (
    <Box className={unitTaskCardStyle({ status, active, class: className })} testID={testID}>
        <Box className='flex-row items-center justify-between gap-3'>
            <Text size='body' tone='default' className='uppercase'>
                {title}
            </Text>
            {statusSlot}
        </Box>
        {children}
        {footer && (
            <Box className='items-end'>
                {typeof footer === 'string' ? (
                    <Text size='caption' tone='muted'>
                        {footer}
                    </Text>
                ) : (
                    footer
                )}
            </Box>
        )}
    </Box>
)

UnitTaskCard.displayName = 'UnitTaskCard'
